<template>
  <div>
    <div class="d-flex button-group"
         :class="{'justify-content-end': isNewOperation, 'justify-content-between': !isNewOperation}">
      <div v-if="!isNewOperation" class="button-group margin-0 d-flex">
        <history-modal></history-modal>
        <b-btn class="history-btn" @click="openHistoryModal" size="sm">
          {{$t('buttons.common.history')}}
        </b-btn>
        <documents-templates-modal template-type="operation"
                                   file-upload-url="woodware/generate_excel_to_movements/"></documents-templates-modal>
        <b-btn variant="outline-secondary" title="Print documents" class="d-inline-flex" size="sm"
               @click="openTemplatesModal">
          <icon name="print"></icon>
        </b-btn>
      </div>
      <div class="button-group margin-0 d-flex">
        <template v-if="!isNewOperation">
          <b-btn variant="primary" size="sm" title="Add purchase invoice" class="mr-2"
                 v-if="isApprovedOperation && isAccept && permissionsList.purchase_invoices.change_purchaseinvoice"
                 @click="addPurchaseInvoice">
            {{$t('buttons.purchase_invoices.add_purchase_invoice')}}
          </b-btn>
          <div>
            <b-btn variant="outline-primary" size="sm" @click="changePackagesLocation('return_packages_from_stock')"
                   v-if="isActiveOperation"
                   title="Return selected packages from stock">
              {{$t('buttons.operations.return_from_stock')}}
            </b-btn>
            <b-btn variant="outline-primary" size="sm" v-else
                   title="Turn back operation to previous status"
                   @click="changeOperationStatus('move_status_back')">
              {{$t('buttons.operations.turn_back')}}
            </b-btn>
          </div>
          <b-btn variant="outline-warning" size="sm" v-if="canBePartialCompleted"
                 title="Move packets to stock. Operation status doesn't change"
                 @click="changePackagesLocation('move_packages_to_stock')">
            {{$t('buttons.operations.partial_complete')}}
          </b-btn>
          <b-btn variant="success" size="sm" v-if="isApprovedOperation" title="Approve operation"
                 @click="changeOperationStatus('move_status_forward')"> {{$t('buttons.operations.approve')}}
          </b-btn>
          <b-btn variant="success" size="sm" title="Complete operation" v-if="isActiveOperation"
                 @click="completeOperation">{{$t('buttons.operations.complete')}}
          </b-btn>
          <b-btn variant="outline-danger" size="sm" title="Delete operation" v-if="isActiveOperation"
                 @click="deleteOperation">{{ $t('buttons.common.delete')}}
          </b-btn>
        </template>
        <b-btn variant="outline-success" size="sm" @click="save" title="Save operation"
               v-if="!isHistoryOperation">{{ $t('buttons.common.save')}}
        </b-btn>
      </div>
    </div>
    <div class="d-flex justify-content-between toggle-container" @click="showCollapse  = !showCollapse "
         :title="operationData.auto_calc === false ? 'Auto calc is off' : 'Auto calc is on'"
         :aria-expanded="showCollapse  ? 'true' : 'false'"
         aria-controls="collapse"
         :class="{'off-auto-calc': operationData.auto_calc === false}">
      <h3>{{operationLabel}}</h3>
      <div class="icons-wrapper">
        {{operationInfo}}
        <icon name="angle-down" class="angle" v-if="showCollapse"></icon>
        <icon name="angle-right" class="angle" v-else></icon>
      </div>
    </div>
    <b-collapse id="collapse" v-model="showCollapse">
      <div class="form-info">
        <div class="col">
          <div v-if="!isHiddenFields('process')">
            <label>{{$t('labels.operations.operation_type')}}</label>
            <multiselect v-model="operationData.process" :max-height="200"
                         placeholder="" label="name" track-by="name" :disabled="isNotEditableField('process')"
                         :class="{ required: !operationData.process.name && isLoadedData}"
                         select-label="" deselect-label="" selected-label=""
                         :options="processes" :allow-empty="false"></multiselect>
          </div>
          <div v-if="!isHiddenFields('start_date')">
            <label>{{$t('labels.common.start_date')}}</label>
            <datepicker v-model="operationData.start_date" lang="en" placeholder=""
                        :width="170" :first-day-of-week="1" :not-after="new Date()"
                        :class="{ required: !operationData.start_date && isLoadedData}"
                        :disabled="isNotEditableField('start_date')"
            ></datepicker>
          </div>
          <div v-if="!isNewOperation && !isHiddenFields('end_date')">
            <label>{{$t('labels.common.end_date')}}</label>
            <datepicker v-model="operationData.end_date" lang="en" :width="170" :first-day-of-week="1"
                        :disabled="true" placeholder=""></datepicker>
          </div>
          <div class="checkbox-wrapper" v-if="!isNewOperation && !isHiddenFields('auto_calc')">
            <input id="auto-calc" type="checkbox" v-model="operationData.auto_calc"
                   @change="changeAutoCalculation" :disabled="isNotEditableField('auto_calc')">
            <label for="auto-calc"
                   class="margin-left-5">{{$t('labels.operations.auto_calc')}}</label>
          </div>
        </div>
        <div class="col">
          <div v-if="!isHiddenFields('distributor')">
            <label>{{$t('labels.common.supplier')}}/{{$t('labels.common.buyer')}}</label>
            <multiselect v-model="operationData.distributor" :max-height="200"
                         placeholder=""
                         label="name" track-by="name" @input="selectBuyer"
                         :options="distributors"
                         select-label="" deselect-label="" selected-label=""
                         :disabled="isNotEditableField('distributor')"></multiselect>
          </div>
          <div v-if="!isNewOperation && !isHiddenFields('contract')">
            <label>{{$t('labels.common.contract_nr')}}</label>
            <multiselect v-model="operationData.contract" :max-height="200"
                         placeholder="" label="contract_nr" track-by="contract_nr"
                         :options="purchaseContracts"
                         :disabled="isNotEditableField('contract')"
                         select-label="" deselect-label="" selected-label="">
            </multiselect>
          </div>
          <div v-if="!isNewOperation && !isHiddenFields('sales_order')">
            <label>{{$t('labels.common.order')}}</label>
            <multiselect v-model="operationData.sales_order" label="descriptor"
                         track-by="descriptor" placeholder=""
                         open-direction="bottom"
                         :options="salesOrders" :internal-search="true" :max-height="200"
                         :searchable="true" :loading="isLoadingOrders" :hide-selected="false"
                         @select="selectSalesOrder" @search-change="asyncSalesOrdersSearch"
                         :disabled="isNotEditableField('sales_order')">
              <template slot="clear" v-if="isActiveOperation || isNewOperation">
                <div class="multiselect__clear" @mousedown.prevent.stop="clearOrder()">❌</div>
              </template>
              <span slot="noResult">No elements found. Consider changing the search query.</span>
            </multiselect>
          </div>
          <div v-if="!isNewOperation && !isHiddenFields('delivery_address')">
            <partner-contact-modal :id="buyerId" :fetchDeliveryAddresses="fetchDeliveryAddresses">
            </partner-contact-modal>
            <label>{{$t('labels.common.delivery_address')}}</label>
            <div class="d-flex">
              <multiselect v-model="operationData.delivery_address" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="delivery_addresses"
                           :disabled="isNotEditableField('delivery_address')"
                           select-label="" deselect-label="" selected-label=""></multiselect>
              <div class="icons-group vertical-center-container" @click="openPartnerContactModal"
                   :disabled="isNotEditableField('delivery_address')">
                <icon name="plus-circle"></icon>
              </div>
            </div>
          </div>
          <div v-if="!isHiddenFields('stock_address')">
            <label>{{$t('labels.common.stock')}}</label>
            <multiselect v-model="operationData.stock_address" placeholder=""
                         label="name" track-by="name" :max-height="200" :allow-empty="false"
                         :options="stock_addresses"
                         :disabled="isNotEditableField('stock_address')"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
        </div>
        <div class="col">
          <div v-if="!isHiddenFields('wobject')">
            <label>{{$t('labels.common.production_place')}}</label>
            <multiselect v-model="operationData.wobject" :max-height="200" label="name" track-by="name"
                         placeholder="" :options="production_places"
                         :disabled="isNotEditableField('wobject')"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div v-if="!isHiddenFields('executors')">
            <label>{{$t('labels.common.executor')}}</label>
            <multiselect v-model="operationData.executors" :max-height="200"
                         placeholder="" :multiple="true" :close-on-select="false"
                         label="username" track-by="username" :options="users"
                         :disabled="isNotEditableField('executors')"
                         select-label="" deselect-label="" selected-label="">
            </multiselect>
          </div>
          <div v-if="!isNewOperation && !isHiddenFields('CMR')">
            <label>CMR</label>
            <input class="form-control" v-model="operationData.CMR" :disabled="isNotEditableField('CMR')">
          </div>
          <div v-if="!isNewOperation && !isHiddenFields('delivery_note')">
            <label>{{$t('labels.common.delivery_number')}}</label>
            <input class="form-control" v-model="operationData.delivery_note"
                   :disabled="isNotEditableField('delivery_note')">
          </div>
          <div v-if="!isNewOperation && !isHiddenFields('purchase_invoice')">
            <label>{{$t('common_content.purchase_invoice')}}</label>
            <div>
              <router-link :to="`/purchase-invoice/${operationData.purchase_invoice.id}`"
                           v-if="operationData.purchase_invoice">
                {{operationData.purchase_invoice && operationData.purchase_invoice.invoice_number}}
              </router-link>
              <span v-else style="color: #e85c5c">{{$t('notifications.purchase_invoices.no_related_invoice')}}</span>
            </div>
          </div>
        </div>
        <div class="col">
          <div v-if="!isNewOperation && !isHiddenFields('transport')">
            <label>{{$t('labels.common.transport_nr')}}</label>
            <input class="form-control" v-model="operationData.transport"
                   :disabled="isNotEditableField('transport')">
          </div>
          <div v-if="!isNewOperation && !isHiddenFields('container_number')">
            <label>{{$t('labels.common.container_nr')}}</label>
            <input class="form-control" v-model="operationData.container_number"
                   :disabled="isNotEditableField('container_number')">
          </div>
          <div v-if="!isNewOperation && !isHiddenFields('driver_name')">
            <label>{{$t('labels.common.driver_name')}}</label>
            <vue-suggest :list="shipment_driver_names" :filter-by-query="true" @select="selectDriver"
                         mode="input">
              <input class="form-control" v-model="operationData.driver_name"
                     :disabled="isNotEditableField('driver_name')">
            </vue-suggest>
          </div>
          <div v-if="!isNewOperation && !isHiddenFields('carrier')">
            <label>{{$t('labels.common.carrier')}}</label>
            <multiselect v-model="operationData.carrier" :max-height="200" placeholder=""
                         label="name" track-by="name" :options="carriers"
                         :disabled="isNotEditableField('carrier')"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div v-if="!isHiddenFields('comment')">
            <label>{{$t('labels.common.remark')}}</label>
            <b-form-textarea v-model="operationData.comment"
                             :disabled="isNotEditableField('comment')"></b-form-textarea>
          </div>
        </div>
      </div>
    </b-collapse>
    <b-card no-body v-if="!isNewOperation">
      <package-modal :fetchCallback="fetchPackagesData" :operationId="param">
        <modal-navs :listData="toStockPackagedModified"
                    :currentItemIndex="currentPackageIndex"
                    closeModalEventName="closePackageModal" :iterateCallBack="iteratePackageCallBack"
                    prevBtnTitle="Previous package" nextBtnTitle="Next package"
                    v-if="currentTabName === 'to-stock'"
        ></modal-navs>
      </package-modal>
      <price-editing-modal :operationId="param" :fetchCallback="fetchPackagesData"></price-editing-modal>
      <history-packet-modal></history-packet-modal>
      <b-tabs card v-if="isShowTabs">
        <b-tab v-if="tabsRestrictions['global']"
               @click="activeTab([globalGridToStock, globalGridFromStock], 'global')">
          <template slot="title">
            <portal-target name="global-tab"></portal-target>
          </template>
          <portal to="global-tab">
            {{$t('tabs.operations.from_stock')}} / {{$t('tabs.operations.to_stock')}}
          </portal>
          <b-row v-if="!isGridsFullWidth">
            <b-col class="d-flex justify-content-end">
              <img @click="changeFullWidth"
                   src="@/assets/images/common-icons/horizontal-split.png"
                   class="grid-size-toggle" title="Arrange the tables in a horizontal position">
            </b-col>
          </b-row>
          <b-row :class="{'d-flex': !isGridsFullWidth, 'd-block': isGridsFullWidth}" style="margin-top: 0">
            <b-col>
              <div class="d-flex justify-content-between align-items-center grid-label">
                <label>{{$t('labels.operations.from_stock')}}</label>
                <div class="d-flex">
                  <div class="d-flex justify-content-end align-items-center">
                    <span v-for="item in globalFromStockTotalData" :key="item.text"
                          class="total-info__item"
                          style="margin-left: 8px">
                      {{$t(`labels.common.${item.label}`)}}: {{item.value.toLocaleString('ru-RU')}}
                    </span>
                  </div>
                  <save-columns-btn :grid-name="fromStockGridName" v-show="isGridsFullWidth"
                                    :grid="globalGridFromStock"
                                    class="margin-left-5"></save-columns-btn>
                  <img @click="changeFullWidth" v-if="isGridsFullWidth"
                       src="@/assets/images/common-icons/vertical-split.png"
                       class="grid-size-toggle" title="Arrange the tables in a vertical position">
                </div>
              </div>
              <ag-grid-vue class="ag-theme-balham grid" :style="!isGridsFullWidth && gridHeight"
                           :gridOptions="globalGridFromStock"
                           :rowData="fromStockPackages" :columnDefs="globalShowingFromStockColumns"
                           :pinnedBottomRowData="fromStockPinnedData"
                           @filterChanged="filterModified(globalGridFromStock, 'globalGridFromStock')"
                           @rowDoubleClicked="openHistoryPacketDialog" :postProcessPopup="postProcessPopup">
              </ag-grid-vue>
            </b-col>
            <b-col>
              <div class="d-flex justify-content-between align-items-center grid-label">
                <label>{{$t('labels.operations.to_stock')}}</label>
                <div class="d-flex">
                  <div class="d-flex justify-content-end margin-0 align-items-center">
                    <span v-for="item in globalToStockTotalData" :key="item.text"
                          class="total-info__item"
                          style="margin-left: 8px">
                      {{$t(`labels.common.${item.label}`)}}: {{item.value.toLocaleString('ru-RU')}}
                    </span>
                  </div>
                  <save-columns-btn :grid-name="toStockGridName" v-show="isGridsFullWidth"
                                    :grid="globalGridToStock"
                                    class="margin-left-5"></save-columns-btn>
                </div>
              </div>
              <div ref="to-stock-grid">
                <ag-grid-vue class="ag-theme-balham grid" :style="!isGridsFullWidth && gridHeight"
                             :gridOptions="globalGridToStock" :class="{'with-row-style-priority': !isActiveOperation}"
                             :rowData="toStockPackages" :columnDefs="globalShowingToStockColumns"
                             @rowDoubleClicked="fetchPackageData($event, 'global')"
                             :pinnedBottomRowData="toStockPinnedData"
                             @filterChanged="filterModified(globalGridToStock, 'globalGridToStock')"
                             @selectionChanged="isApprovedOperation && toStockSelectionChanged()"
                             :postProcessPopup="postProcessPopup"
                ></ag-grid-vue>
              </div>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab v-if="tabsRestrictions['from-stock']" @click="activeTab([fromStockGrid], 'from-stock')">
          <template slot="title">
            <portal-target name="from-stock-tab"></portal-target>
          </template>
          <portal to="from-stock-tab">
            {{$t('tabs.operations.from_stock')}}
          </portal>
          <b-popover target="package-settings-btn" placement="bottom" ref="package-settings-btn-popover">
            <div class="d-flex flex-column">
              <div class="d-flex align-items-center">
                <input type="checkbox" class="ml-2 mb-0" :checked="isSelectPacket" @change="selectPacket">
                <label class="margin-left-5">{{$t('labels.operations.highlight_package')}}</label>
              </div>
              <div class="d-flex align-items-center mt-2">
                <input type="checkbox" class="ml-2 mb-0" v-model="clonePackages">
                <label class="margin-left-5">{{$t('labels.operations.duplicate_package_nr')}}</label>
              </div>
            </div>
          </b-popover>
          <b-row v-show="isActiveOperation">
            <stock-modal :fetchCallback="fetchPackagesData" :operationId="param"></stock-modal>
            <b-col class="d-flex align-items-center" cols="3">
              <b-btn size="sm" @click="showStockModal" title="Show packages in stock">
                <icon name="cubes"></icon>
                {{$t('buttons.common.show_stock')}}
              </b-btn>
              <b-btn variant="danger" class="ml-2" size="sm" title="Delete selected packages"
                     @click="deletePackages(fromStockGrid)">{{$t('buttons.common.delete')}}
              </b-btn>
            </b-col>
            <b-col class="d-flex align-items-center justify-content-center" cols="6">
              <input class="form-control ml-2" ref="package_nr"
                     @keyup.enter="addPackageToOperation" style="width: 300px;">
              <icon class="package-settings-icon ml-2" id="package-settings-btn" name="cog"></icon>
            </b-col>
            <b-col class="d-flex justify-content-end" cols="3">
            </b-col>
          </b-row>
          <b-row>
            <b-col class="d-flex justify-content-end align-items-center">
              <span v-for="item in fromStockTotalData" :key="item.text" class="total-info__item"
                    style="margin-left: 8px">
                {{$t(`labels.common.${item.label}`)}}: {{item.value.toLocaleString('ru-RU')}}
              </span>
              <save-columns-btn :grid-name="fromStockGridName" :grid="fromStockGrid"
                                class="margin-left-5"></save-columns-btn>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <ag-grid-vue class="ag-theme-balham grid" :style="gridHeight" :gridOptions="fromStockGrid"
                           :rowData="fromStockPackages" :columnDefs="showingFromStockColumns"
                           :pinnedBottomRowData="fromStockPinnedData"
                           @rowDoubleClicked="openHistoryPacketDialog"
                           @filterChanged="filterModified(fromStockGrid, 'fromStockGrid')"
                           :postProcessPopup="postProcessPopup">
              </ag-grid-vue>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab v-if="tabsRestrictions['to-stock']" @click="activeTab([toStockGrid], 'to-stock')">
          <template slot="title">
            <portal-target name="to-stock-tab"></portal-target>
          </template>
          <portal to="to-stock-tab">
            {{$t('tabs.operations.to_stock')}}
          </portal>
          <b-popover target="create-by-template" placement="bottom" ref="create-by-template-popover">
            <div class="d-flex">
              <input value="1" type="number" class="form-control margin-right-5"
                     ref="packagesAtTemplateCount">
              <b-btn variant="warning" size="sm" title="Create package by selected package template"
                     @click="createByTemplate">{{$t('buttons.common.create')}}
              </b-btn>
            </div>
          </b-popover>
          <b-popover target="add-purchase-price" placement="bottom" ref="add-purchase-price-popover">
            <div class="d-flex">
              <input class="form-control mr-1" ref="purchasePriceForPackages">
              <b-btn variant="success" size="sm" title="Create purchase Price"
                     @click="addPurchasePrice">{{$t('buttons.common.save')}}
              </b-btn>
            </div>
          </b-popover>
          <adding-purchase-cost-to-package-modal
              :fetchCallback="fetchPackagesData"></adding-purchase-cost-to-package-modal>
          <b-row v-show="isActiveOperation">
            <b-col cols="7" class="d-flex">
              <div class="button-group d-flex m-0">
                <b-btn variant="success" size="sm" id="create-package" title="Create pellets package"
                       @click="createNewPackage">{{$t('buttons.common.add')}}
                </b-btn>
                <b-btn variant="danger" size="sm" title="Delete selected packages"
                       @click="deletePackages(toStockGrid)">{{$t('buttons.common.delete')}}
                </b-btn>
              </div>
            </b-col>
            <b-col class="d-flex">
              <select class="form-control templates-list mr-2"
                      v-model="currentProductTemplate" title="Selected needed template">
                <option v-for="template in product_templates"
                        :key="template.id" :value="template">{{template.name}}
                </option>
              </select>
              <div class="button-group d-flex m-0" cols="5">
                <b-btn variant="outline-success" size="sm" id="create-by-template"
                       :disabled="!currentProductTemplate">{{$t('buttons.operations.create_by_template')}}
                </b-btn>
                <b-btn variant="outline-danger" title="Remove selected template" size="sm"
                       @click="deleteProductTemplate" :disabled="!currentProductTemplate">
                  {{$t('buttons.operations.remove_template')}}
                </b-btn>
              </div>
            </b-col>
            <b-col cols="2"></b-col>
          </b-row>
          <b-row v-show="isApprovedOperation && !isPelletizing">
            <b-col>
              <input enctype="multipart/form-data" type="file" ref="priceFile" @change="loadPriceFile"
                     accept=".xlsx, .xls, .csv">
              <b-btn size="sm" variant="outline-dark" id="add-purchase-price">
                {{$t('buttons.operations.operation_purchase_price')}}
              </b-btn>
              <b-btn size="sm" variant="outline-secondary" @click="chooseFile"
                     title="Import prices from excel document">
                <icon name="file-import" class="margin-right-5"></icon>
                {{$t('buttons.operations.import_prices')}}
              </b-btn>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="d-flex justify-content-end align-items-center">
              <span v-for="item in toStockTotalData" :key="item.text" class="total-info__item"
                    style="margin-left: 8px">
                {{$t(`labels.common.${item.label}`)}}: {{item.value.toLocaleString('ru-RU')}}
              </span>
              <save-columns-btn :grid-name="toStockGridName" class="margin-left-5"
                                :grid="toStockGrid"></save-columns-btn>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <ag-grid-vue class="ag-theme-balham grid" :style="gridHeight" :gridOptions="toStockGrid"
                           :class="{'with-row-style-priority': !isActiveOperation}"
                           :rowData="toStockPackages" :columnDefs="showingToStockColumns"
                           @rowDoubleClicked="fetchPackageData($event, 'to-stock')"
                           :pinnedBottomRowData="toStockPinnedData"
                           @selectionChanged="isApprovedOperation && isPelletizing && toStockSelectionChanged"
                           @filterModified="filterModified(toStockGrid, 'toStockGrid')"
                           @filterChanged="toStockModify" @sortChanged="toStockModify"
                           :postProcessPopup="postProcessPopup">
              </ag-grid-vue>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab v-if="tabsRestrictions['grouping']" @click="activeTab([groupingsGrid], 'grouping')">
          <template slot="title">
            <portal-target name="groupings-tab"></portal-target>
          </template>
          <portal to="groupings-tab">
            {{$t('tabs.operations.groupings')}}
          </portal>
          <grouping-modal :operationId="param" :fetchCallback="fetchPackagesData"></grouping-modal>
          <b-row v-show="!isHistoryOperation">
            <b-col class="d-flex justify-content-end">
              <b-btn variant="success" size="sm" title="Add grouping"
                     @click="openEditGroupingDialog({isNew: true})">
                {{$t('buttons.common.add')}}
              </b-btn>
              <b-btn variant="danger" class="margin-left-5" size="sm"
                     title="Delete selected groupings" @click="deleteGroupings">
                {{$t('buttons.common.delete')}}
              </b-btn>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <ag-grid-vue class="ag-theme-balham grid" :style="gridHeight" :gridOptions="groupingsGrid"
                           :rowData="operationGroupings" :columnDefs="groupingsGridColumns"
                           @rowDoubleClicked="fetchGroupingData" :postProcessPopup="postProcessPopup">
              </ag-grid-vue>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab v-if="tabsRestrictions['scores']" @click="activeTab([scoresGrid], 'scores')">
          <template slot="title">
            <portal-target name="scores-tab"></portal-target>
          </template>
          <portal to="scores-tab">
            {{$t('tabs.operations.scores')}}
          </portal>
          <b-row>
            <b-col>
              <ag-grid-vue class="ag-theme-balham grid" :style="gridHeight" :gridOptions="scoresGrid"
                           :columnDefs="scoreColumns" :rowData="scores" :postProcessPopup="postProcessPopup">
              </ag-grid-vue>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab v-if="tabsRestrictions['fixed-price']" @click="activeTab([fixedPriceGrid], 'fixed-price')">
          <template slot="title">
            <portal-target name="fixed-price-tab"></portal-target>
          </template>
          <portal to="fixed-price-tab">
            {{$t('tabs.operations.fixed_price')}}
          </portal>
          <b-row v-if="!isHistoryOperation">
            <b-col class="d-flex justify-content-end">
              <b-btn variant="success" size="sm" title="Add fixed price"
                     @click="openPriceEditModal('fixed_prices', {isNew: true})">{{$t('buttons.common.add')}}
              </b-btn>
              <b-btn variant="danger" class="margin-left-5" size="sm"
                     title="Delete selected fixed prices"
                     @click="deleteFixedPrice">{{$t('buttons.common.delete')}}
              </b-btn>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <ag-grid-vue class="ag-theme-balham grid" :gridOptions="fixedPriceGrid"
                           :rowData="fixedPrices" :columnDefs="fixedPriceColumns"
                           @rowDoubleClicked="(params) => !isHistoryOperation && fetchFixedPriceData(params)"
                           :postProcessPopup="postProcessPopup">
              </ag-grid-vue>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab v-if="tabsRestrictions['operation-cost']" @click="activeTab([operationCostGrid], 'operation-cost')">
          <template slot="title">
            <portal-target name="operation-cost-tab"></portal-target>
          </template>
          <portal to="operation-cost-tab">
            {{$t('tabs.operations.operations_cost')}}
          </portal>
          <b-row v-if="!isHistoryOperation">
            <b-col class="d-flex justify-content-end">
              <b-btn variant="success" size="sm" title="Add operation cost"
                     @click="openPriceEditModal('process_prices', {isNew: true})">
                {{$t('buttons.common.add')}}
              </b-btn>
              <b-btn variant="danger" class="margin-left-5" size="sm"
                     title="Delete selected operation costs" @click="deleteProcessPrice">
                {{$t('buttons.common.delete')}}
              </b-btn>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <ag-grid-vue class="ag-theme-balham grid" :style="gridHeight" :gridOptions="operationCostGrid"
                           :rowData="operationCosts" :columnDefs="operationCostsColumns"
                           @rowDoubleClicked="(params) => !isHistoryOperation && fetchOperationCostData(params)"
                           :postProcessPopup="postProcessPopup">
              </ag-grid-vue>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab v-if="tabsRestrictions['additional-cost']" @click="activeTab([additionalCostsGrid], 'additional-cost')">
          <template slot="title">
            <portal-target name="additional-cost-tab"></portal-target>
          </template>
          <portal to="additional-cost-tab">
            {{$t('tabs.common.additional_cost')}}
          </portal>
          <addition-cost-modal :operationId="this.param"
                               :fetchCallback="fetchOperationData"></addition-cost-modal>
          <b-row v-if="isApprovedOperation">
            <b-col class="d-flex justify-content-end">
              <b-btn variant="success" size="sm" @click="openAdditionalCostModal({isNew: true})">
                {{$t('buttons.common.add')}}
              </b-btn>
              <b-btn variant="danger" class="margin-left-5" size="sm"
                     title="Delete selected additional costs" @click="deleteAdditionalCost">
                {{$t('buttons.common.delete')}}
              </b-btn>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <ag-grid-vue class="ag-theme-balham grid" :style="gridHeight" :gridOptions="additionalCostsGrid"
                           :columnDefs="additionalCostsGridColumns" :rowData="additionalCosts"
                           @rowDoubleClicked="(params) => isApprovedOperation && fetchAdditionalCostData(params)"
                           :postProcessPopup="postProcessPopup">
              </ag-grid-vue>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab v-if="isLoadedData" @click="fetchDocuments(true)">
          <template slot="title">
            <portal-target name="documents-tab"></portal-target>
          </template>
          <portal to="documents-tab">
            {{$t('tabs.common.documents')}}
          </portal>
          <app-documents :isCollapse="showCollapse" :documents="documents" :url="`${commonUrl}attachments/`"
                         @loadDocuments="fetchDocuments"></app-documents>
        </b-tab>
        <b-tab v-if="tabsRestrictions['labels']" @click="activeLabelsTab">
          <template slot="title">
            <portal-target name="labels-tab"></portal-target>
          </template>
          <portal to="labels-tab">
            {{$t('tabs.operations.labels')}}
          </portal>
          <b-popover target="labels-selected-columns" placement="left" ref="labels-selected-columns-popover">
            <div class="d-flex justify-content-between">
              <multiselect v-model="printLabelFields"
                           label="headerName" track-by="headerName" :options="optionsLabelColumns"
                           placeholder="" select-label="" deselect-label="" selected-label=""
                           :multiple="true" :close-on-select="false"
              ></multiselect>
              <b-btn size="sm" variant="outline-info" @click="printLabels" class="margin-left-5"
                     title="Print labels with selected columns">
                <icon name="file-pdf"></icon>
              </b-btn>
            </div>
          </b-popover>
          <b-row>
            <b-col class="d-flex justify-content-between">
              <b-btn size="sm" variant="outline-secondary" title="Select tha last placed on the stock"
                     @click="selectLastPlacedPackets" class="margin-right-5"
                     :class="{'hidden': !isActiveOperation}">
                {{$t('buttons.operations.select_last_placed')}}
              </b-btn>
              <div class="d-flex align-items-center">
                <span class="ad-block-warning" v-show="!isAdBlockOff">
                  AdBlock is turned on on this page.
                  Please turn off AdBlock for this page and reload the page for correct pdf label generation
                </span>
                <b-btn size="sm" id="labels-selected-columns">
                  {{$t('buttons.operations.print_labels')}}
                </b-btn>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <ag-grid-vue class="ag-theme-balham grid" :style="gridHeight" :gridOptions="labelsGrid"
                           :rowData="toStockPackages" :columnDefs="labelsColumns"
                           :postProcessPopup="postProcessPopup">
              </ag-grid-vue>
            </b-col>
          </b-row>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex';
  import { AgGridVue } from 'ag-grid-vue';
  import Multiselect from 'vue-multiselect';
  import Datepicker from 'vue2-datepicker';
  import VueSuggest from 'vue-simple-suggest';
  import 'vue-simple-suggest/dist/styles.css';
  import Documents from '@/components/common/Documents';
  import PartnerContactModal from '@/components/modals/PartnerContactModal';
  import AdditionCostModal from '@/components/modals/AdditionalCost';
  import PackageModal from '@/components/modals/PackageModal';
  import PriceEditingModal from '@/components/modals/PriceEditingModal';
  import GroupingModal from '@/components/modals/GroupingEditModal';
  import AddingPurchaseCostToPackageModal from '@/components/modals/AddingPurchaseCostToPacket';
  import HistoryModal from '@/components/modals/HistoryModal';
  import DocumentsTemplatesModal from '@/components/modals/DocumentsTemplatesModal';
  import HistoryPacketModal from '@/components/modals/HistoryPacketModal';
  import StockModal from '@/components/modals/StockModal';
  import saveColumnsBtn from '@/components/common/SaveColumnsBtn';
  import ModalNavs from '@/components/common/ModalNavs';

  import {
    handlerResponse,
    setNotifyData,
    getFilteredNodes,
    getFilteredSelectedNodes,
    getFilteredSelectedData, getArrayByProperty
  } from '@/helpers/commonHelpers';
  import {
    notificationsRenderer,
    autoResize
  } from '@/helpers/renderHelpers';
  import { translateColumns } from '@/helpers/translate-helper';
  import { generateManyPagesForContent } from '@/helpers/createPdfLabel';

  import moment from 'moment';
  import { gridMixins } from '@/mixins';
  import { DATE_FORMAT } from '@/constants';

  export default {
    mixins: [gridMixins],
    components: {
      Multiselect, AgGridVue, Datepicker, VueSuggest, PartnerContactModal, AdditionCostModal,
      PackageModal, PriceEditingModal, GroupingModal, AddingPurchaseCostToPackageModal, 'app-documents': Documents,
      HistoryModal, DocumentsTemplatesModal, HistoryPacketModal, saveColumnsBtn, ModalNavs, StockModal
    },
    data() {
      return {
        isLoadedData: true,
        showCollapse: false,
        isLoadingOrders: false,
        purchaseContracts: [],
        salesOrders: [],
        delivery_addresses: [],
        operationData: {},
        currentProductTemplate: null,
        isGridsFullWidth: false,
        isShowTabs: false,
        currentPackage: null,
        printLabelFields: [
          {
            field: 'pallet_size',
            headerName: this.$i18n.t('tables.pallet_size')
          },
          {
            field: 'diameter_name',
            headerName: this.$i18n.t('tables.diameter')
          },
          {
            field: 'tree_name',
            headerName: this.$i18n.t('tables.wood')
          },
          {
            field: 'partner_name',
            headerName: this.$i18n.t('tables.partner_name')
          },
          {
            field: 'moved_to_stock_date',
            headerName: this.$i18n.t('tables.start_date')
          },
          {
            field: 'unit_weight',
            headerName: this.$i18n.t('tables.unit_weight')
          },
          {
            field: 'pieces',
            headerName: this.$i18n.t('tables.pieces')
          },
          {
            field: 'weight',
            headerName: this.$i18n.t('tables.total_weight')
          }
        ],
        isAdBlockOff: true,
        currentTabName: '',
        toStockGridSortedModel: null,
        toStockGridFilteredModel: null,
        toStockPackagedModified: [],
        clonePackages: false
      }
    },
    methods: {
      operationDataInitialize() {
        this.operationData = {
          status: {},
          process: {},
          name: '',
          executors: [],
          stock_address: null,
          start_date: new Date(),
          end_date: null,
          creator: '',
          document_nr: '',
          distributor: null,
          comment: '',
          transport: '',
          container_number: '',
          delivery_note: '',
          wobject: null,
          carrier: null,
          sales_order: null,
          delivery_address: null,
          contract: null,
          CMR: '',
          driver_name: '',
          user: '',
          additional_costs: []
        }
      },
      prepareSavedData() {
        return {
          process: this.operationData.process && this.operationData.process.id,
          comment: this.operationData.comment,
          start_date: this.operationData.start_date ? moment(this.operationData.start_date).format(DATE_FORMAT) : '',
          container_number: this.operationData.container_number,
          executors: getArrayByProperty(this.operationData.executors, 'id'),
          wobject: this.operationData.wobject && this.operationData.wobject.id,
          stock_address: this.operationData.stock_address && this.operationData.stock_address.id,
          transport: this.operationData.transport,
          distributor: this.buyerId,
          carrier: this.operationData.carrier && this.operationData.carrier.id,
          sales_order: this.operationData.sales_order && this.operationData.sales_order.id,
          delivery_address: this.operationData.delivery_address && this.operationData.delivery_address.id,
          contract: this.operationData.contract && this.operationData.contract.id,
          delivery_note: this.operationData.delivery_note,
          CMR: this.operationData.CMR,
          driver_name: this.operationData.driver_name,
          packages: this.$route.query.packages // bounded packages to created operation
        }
      },
      save() {
        const savedData = this.prepareSavedData();
        if (this.isNewOperation) {
          this.$http.post(`woodware/movements/`, savedData).then(handlerResponse).then((response) => {
            this.operationData = {...this.operationData, ...response};
            this.$router.push({path: `/operation/${response.id}`});
            this.$notify(setNotifyData(this.$i18n.t(`notifications.common.success`),
              this.$i18n.t(`notifications.operations.operation_created`), 'success'));
            this.$store.dispatch(`woodware/fetchOperations`);
          }).catch(error => notificationsRenderer(error.data, 'error'));
        } else {
          this.updateOperation(savedData);
        }
      },
      async completeOperation() {
        const savedData = this.prepareSavedData();
        const prevOperationStatus = this.operationData.status.key;
        try {
          await this.updateOperation(savedData);
          const response = await this.changeOperationStatus('move_status_forward', false);
          const {status} = response;
          if (this.operationData.generate_barcodes) {
            this.$swal.fire({
              title: this.$i18n.t(`dialogs.print_labels`),
              icon: 'question',
              showCancelButton: true,
              confirmButtonColor: '#1bc14d',
              cancelButtonColor: '#d33',
              confirmButtonText: this.$i18n.t(`common_content.yes`),
              cancelButtonText: this.$i18n.t(`common_content.no`)
            }).then(result => {
              if (result.value) {
                const packages = this.toStockPackages.map(item => item.id);
                const columns = this.printLabelFields.map(item => item.field);
                const payload = {columns, packages};
                this.getPdfLabels(payload);
                this.redirectToManufacture(status.key, prevOperationStatus);
              } else {
                this.redirectToManufacture(status.key, prevOperationStatus);
              }
            })
          } else {
            this.redirectToManufacture(status.key, prevOperationStatus);
          }
        } catch (e) {
          console.log(e);
        }
      },
      redirectToManufacture(currStatus, prevStatus) {
        if (currStatus === 'P' && prevStatus === 'A') {
          this.$router.push('/packets-search');
        } else if (currStatus === 'C' && prevStatus === 'P') {
          this.$router.push('/operations');
        }
      },
      async changeOperationStatus(move_status, isRedirected = true) {
        const loader = this.$loading.show();
        const prevOperationStatus = this.operationData.status.key;
        try {
          const response = await this.$http.post(`woodware/movements/${this.param}/${move_status}/`).then(handlerResponse);
          this.updateOperationCb(response);
          const {status} = response;
          if (isRedirected) {
            this.redirectToManufacture(status.key, prevOperationStatus);
          }
          return response;
        } catch (error) {
          if (error.data.hasOwnProperty('packages_validation')) {
            this.expandNotValidPackages();
            delete error.data['packages_validation'];
          }
          notificationsRenderer(error.data, 'error');
          throw error.data;
        } finally {
          loader.hide();
        }
      },
      async changePackagesLocation(packages_action) {
        const loader = this.$loading.show();
        try {
          const packages = getFilteredSelectedData(this.toStockGrid);
          const payload = {};
          packages.length && (payload.pckg_id = packages.map(item => item.id));
          const response = await this.$http.post(`woodware/movements/${this.param}/${packages_action}/`, payload)
            .then(handlerResponse);
          this.updateOperationCb(response);
        } catch (error) {
          notificationsRenderer(error.data, 'error');
        } finally {
          loader.hide();
        }
      },
      addPurchaseInvoice() {
        if (!this.operationData.purchase_invoice) {
          this.$router.push({
            name: 'Purchase Invoice',
            params: {
              id: 'new',
              contract: this.operationData.contract && this.operationData.contract.contract_nr,
              supplier: this.operationData.distributor && this.operationData.distributor.name,
              receiving_date: this.operationData.end_date,
              movement_id: this.param
            }
          });
        } else {
          this.$notify(setNotifyData(
            this.$i18n.t(`notifications.operations.receiving_bound_to_other_purchase_invoice`),
            '', 'warn'));
        }
      },
      changeAutoCalculation() {
        this.updateOperation({auto_calc: this.operationData.auto_calc});
      },
      deleteOperation() {
        this.$swal.fire({
          title: this.$i18n.t(`dialogs.delete_operation`),
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#1bc14d',
          cancelButtonColor: '#d33',
          confirmButtonText: this.$i18n.t(`common_content.yes`),
          cancelButtonText: this.$i18n.t(`common_content.no`)
        }).then(result => {
          if (result.value) {
            this.$http.delete(this.commonUrl).then(() => {
              this.$notify(setNotifyData(this.$i18n.t(`notifications.common.success`),
                this.$i18n.t(`notifications.operations.operation_deleted`), 'success'));
              this.$store.dispatch(`woodware/fetchOperations`);
              this.$router.push({path: '/operations'});
            }).catch((error) => notificationsRenderer(error.data, 'error'));
          }
        });
      },
      expandNotValidPackages() {
        this.toStockGrid.api.forEachNode((node) => {
          if (node.parent) {
            if (!node.data.ready_to_stock) {
              node.parent.setExpanded(true);
            }
          }
        });
      },
      async updateOperation(data) {
        try {
          const response = await this.$http.put(this.commonUrl, data).then(handlerResponse);
          this.updateOperationCb(response);
        } catch (error) {
          notificationsRenderer(error.data, 'error');
          throw error.data;
        }
      },
      updateOperationCb(response) {
        this.isShowTabs = false;
        this.$nextTick(() => {
          this.isShowTabs = true;
          this.operationData = {...this.operationData, ...response};
          this.fetchPackagesData();
          this.$notify(setNotifyData(this.$i18n.t(`notifications.common.success`),
            this.$i18n.t(`notifications.operations.operation_updated`), 'success'));
          this.$store.dispatch(`woodware/fetchOperations`);
        });
      },
      async fetchOperationData(isTabsRerender = true) {
        isTabsRerender && (this.isShowTabs = false);
        await this.$http.get(this.commonUrl).then(handlerResponse).then(async (response) => {
          this.operationData = {...this.operationData, ...response};
          this.$store.dispatch('location/setPathName', response.document_nr);
          this.fetchDeliveryAddresses();
          if (!this.isActiveOperation) {
            await this.$store.dispatch('operation/loadScores', this.param);
            this.isShowTabs = true;
            this.fetchPackagesData();
            if (!this.scores.length) {
              this.$store.dispatch('operation/loadOperationGroups', this.param);
            }
            this.$store.dispatch('operation/loadFixedPrices', this.param);
            this.$store.dispatch('operation/loadOperationCosts', this.param);
          } else {
            this.isShowTabs = true;
            this.fetchPackagesData();
          }
        })
      },
      async selectPackagesWithSpreadProcessPrice() {
        if (this.globalGridToStock && this.globalGridToStock.api) {
          this.globalGridToStock.api.forEachNode(node => {
            if (node.data.process_price_spread) {
              node.selected = true; // don't use setSelected(true) because it will call selectionChanged event
            }
          });
          this.globalGridToStock.api.redrawRows();
        }
        if (this.toStockGrid && this.toStockGrid.api) {
          this.toStockGrid.api.forEachNode(node => {
            if (node.data.process_price_spread) {
              node.selected = true; // don't use setSelected(true) because it will call selectionChanged event
            }
          });
          this.toStockGrid.api.redrawRows();
        }
      },
      applyModifiedGridData(grid) {
        if (grid && grid.api) {
          grid.api.setFilterModel(this.toStockGridFilteredModel);
          grid.api.setSortModel(this.toStockGridSortedModel);
        }
      },
      async fetchPackagesData() {
        if (this.isProcessing || this.isPelletizing) {
          await this.$store.dispatch('operation/loadToStockPackages', this.operationData.id);
          this.applyModifiedGridData(this.toStockGrid);
          this.selectPackagesWithSpreadProcessPrice();
          await this.$store.dispatch('operation/loadFromStockPackages', this.operationData.id);
        } else if (this.isShipment) {
          this.$store.dispatch('operation/loadFromStockPackages', this.operationData.id);
        } else if (this.isAccept) {
          await this.$store.dispatch('operation/loadToStockPackages', this.operationData.id);
          this.applyModifiedGridData(this.toStockGrid);
        }
      },
      isSubPackage(params) {
        return params.node.parent.data;
      },
      createNewPackage() {
        const payload = {
          movement: this.operationData.id
        };
        this.$http.post(`woodware/movement_data/`, payload).then(handlerResponse).then((response) => {
          this.fetchPackagesData();
          this.openPackageModal({...response});
        }).catch(error => {
          console.log(error);
        })
      },
      fetchPackageData(params, tabType) {
        let packageId;
        if (this.isSubPackage(params)) {
          packageId = params.node.parent.data.id;
        } else {
          packageId = params.data.id;
        }
        if (!this.isHistoryOperation) {
          if (this.isApprovedOperation && this.isAccept) {
            if (!params.data.children.length) {
              const id = params.data.id;
              this.$http.get(`woodware/movement_data/${id}/purchase_prices/`).then(handlerResponse)
                .then(response => {
                  //this.openAddingPurchaseCostModal(response);
                });
            }
          } else {
            this.$http.get(`woodware/movement_data/${packageId}/`).then(handlerResponse).then(response => {
              if (tabType === 'global') {
                if (this.isApprovedOperation && this.isProcessing) {
                  const payload = {..._.pick(response, ['package_nr']), isNew: true, isCreatePriceToPackage: true};
                  this.openPriceEditModal('fixed_prices', payload);
                } else if (this.isActiveOperation && !this.isShipment) {
                  if (this.isSubPackage(params)) {
                    const subPackageIndex = params.node.childIndex
                    this.openPackageModal(response, subPackageIndex);
                  } else {
                    this.openPackageModal(response);
                  }
                }
              } else if (tabType === 'to-stock') {
                if (this.isPelletizing && !this.isActiveOperation) {
                    const payload = {..._.pick(response, ['package_nr']), isNew: true, isCreatePriceToPackage: true};
                    this.openPriceEditModal('fixed_prices', payload);
                  }
                  if (this.isActiveOperation && !this.isShipment) {
                  if (this.isSubPackage(params)) {
                    const subPackageIndex = params.node.childIndex;
                    this.openPackageModal(response, subPackageIndex);
                  } else {
                    this.openPackageModal(response);
                  }
                  } else if (this.isApprovedOperation && this.isAccept && !this.isPelletizing) {
                    this.openAddingPurchaseCostModal(params.data);
                }
              }
            });
          }
        } else {
          this.openHistoryPacketDialog(params);
        }
      },
      filterModified(grid, gridName) {
        const gridMutationMap = {
          'globalGridFromStock': 'setGlobalFromStockTotal',
          'globalGridToStock': 'setGlobalToStockTotal',
          'fromStockGrid': 'setFromStockTotal',
          'toStockGrid': 'setToStockTotal'
        };
        const data = getFilteredNodes(grid).map(item => item.data).filter(item => !item.parent);
        this.$store.commit(`operation/${gridMutationMap[gridName]}`, {data});
      },
      asyncSalesOrdersSearch(query) {
        if (query) {
          this.isLoadingOrders = true;
          const params = {descriptor: query, limit: 20};
          this.$http.get(`woodware/list/sales_orders_for_movement/${this.param}/`, {params}).then(handlerResponse)
            .then(response => {
              this.salesOrders = response.results;
              this.isLoadingOrders = false;
            });
        }
      },
      selectBuyer() {
        this.operationData.delivery_addresses = null;
        this.fetchDeliveryAddresses();
      },
      selectSalesOrder(selectedOrder) {
        this.operationData.distributor = selectedOrder.partner;
        this.operationData.delivery_addresses = null;
        this.fetchDeliveryAddresses();
      },
      clearOrder() {
        this.operationData.sales_order = [];
        this.operationData.distributor = null;
        this.operationData.delivery_addresses = null;
        this.delivery_addresses = [];
      },
      fetchDeliveryAddresses() {
        const params = {
          partner_id: this.buyerId,
        };
        this.$http.get(`woodware/list/delivery_addresses/`, {params}).then(handlerResponse).then((response) => {
          this.delivery_addresses = response;
        });
      },
      fetchPurchaseContracts() {
        this.$http.get(`purchase/contracts/`).then(handlerResponse).then(response => {
          this.purchaseContracts = response.data.results;
        });
      },
      selectDriver(name) {
        this.operationData.driver_name = name;
      },
      addPackageToOperation() {
        const packageNr = this.$refs['package_nr'].value;
        const payload = {
          movement_id: this.param,
          clone_packets: this.clonePackages
        };
        if (packageNr) {
          if (this.isSelectPacket) this.$refs['package_nr'].select();
          this.$http.put(`woodware/movement_data_add_to_operation_by_package_nr/${packageNr}/`, payload).then(() => {
            this.$notify(setNotifyData(this.$i18n.t(`notifications.common.success`),
              this.$i18n.t(`notifications.packages.package_added_to_operation`), 'success'));
            this.fetchPackagesData();
          }).catch(error => notificationsRenderer(error.data, 'error'));
        } else {
          this.$notify(setNotifyData(this.$i18n.t(`notifications.packages.enter_package_nr_for_adding`), '', 'warn'));
        }
      },
      async showStockModal() {
        this.$root.$emit('openStockModal');
      },
      chooseFile() {
        this.$refs['priceFile'].click();
      },
      loadPriceFile() {
        const priceFile = this.$refs['priceFile'].files[0];
        if (priceFile) {
          const data = new FormData();
          data.append('excel_file', priceFile);
          const headers = {
            'Content-Type': 'multipart/form-data'
          };
          this.$http.post(`woodware/list/import_purchase_prices_from_excel/${this.param}/`, data, {headers}).then(() => {
            this.fetchPackagesData();
            this.$refs['priceFile'].value = '';
          }).catch(error => {
            this.$refs['priceFile'].value = '';
            notificationsRenderer(error.data, 'error')
          });
        }
      },
      createByTemplate() {
        this.closePopovers();
        const copies = this.$refs['packagesAtTemplateCount'].value;
        const payload = {
          copies, movement: this.param, products: [this.currentProductTemplate.product],
          amount: this.currentProductTemplate.amount
        };
        this.$http.post(`woodware/create_movement_data_from_product/`, payload).then(handlerResponse)
          .then((response) => {
            const createdPackagesCount = response.length;
            const templateName = this.currentProductTemplate.name;
            this.$notify(setNotifyData(this.$i18n.t(`notifications.common.success`),
              this.$i18n.t(`notifications.packages.packages_created_by_template`, {
                createdPackagesCount,
                templateName
              }), 'success'));
            this.fetchPackagesData();
          }).catch(error => notificationsRenderer(error.data, 'error'));
      },
      addPurchasePrice() {
        const packets = getFilteredSelectedNodes(this.toStockGrid).map(item => item.data.id);
        if (packets.length) {
          const payload = {
            movement_id: this.param,
            packets,
            purchase_price: Number(this.$refs['purchasePriceForPackages'].value).toFixed(2)
          };
          this.$http.post(`woodware/purchase_price_calculation/`, payload).then(handlerResponse).then(() => {
            this.fetchPackagesData();
            this.$refs['add-purchase-price-popover'].$emit('close');
          }).catch(error => notificationsRenderer(error.data, 'error'));
        } else {
          this.$notify(setNotifyData(this.$i18n.t(`notifications.packages.select`), '', 'warn'));
        }
      },
      deleteProductTemplate() {
        if (this.currentProductTemplate) {
          const templateId = this.currentProductTemplate.id,
            templateName = this.currentProductTemplate.name;
          this.$http.delete(`woodware/product_template/${templateId}/`).then(() => {
            this.currentProductTemplate = null;
            this.$notify(setNotifyData(this.$i18n.t(`notifications.common.success`),
              this.$i18n.t(`notifications.operations.product_template_deleted`, {templateName}), 'success'));
            this.$store.dispatch('woodware/loadProductTemplates');
          }).catch(error => notificationsRenderer(error.data, 'error'));
        }
      },
      closePopovers() {
        this.$refs['create-by-template-popover'] && this.$refs['create-by-template-popover'].$emit('close');
        // this.$refs['create-package-popover'] && this.$refs['create-package-popover'].$emit('close');
      },
      iteratePackageCallBack(id) {
        this.$root.$emit('savePackage');
        this.$http.get(`woodware/movement_data/${id}/`).then(handlerResponse).then(response => {
          this.openPackageModal(response);
        });
      },
      openPackageModal(data, subPackageIndex = 0) {
        this.closePopovers();
        this.currentPackage = data;
        this.$root.$emit('openPackageModal', {...data, subPackageIndex});
      },
      async fetchFixedPriceData(params) {
        try {
          const priceId = params.data.id;
          const priceData = await this.$http.get(`woodware/prices/fixed_prices/${priceId}/`).then(handlerResponse);
          if (priceData && priceData.movement) {
            this.openPriceEditModal('fixed_prices', priceData);
          }
        } catch (e) {
          console.log(e);
        }
      },
      async fetchOperationCostData(params) {
        try {
          const priceId = params.data.id;
          const priceData = await this.$http.get(`woodware/prices/process_prices/${priceId}/`).then(handlerResponse);
          if (priceData && priceData.movement) {
            this.openPriceEditModal('process_prices', priceData);
          }
        } catch (e) {
          console.log(e);
        }
      },
      deleteFixedPrice() {
        const selectedPrice = getFilteredSelectedData(this.fixedPriceGrid)[0];
        if (selectedPrice && selectedPrice.movement) {
          this.$http.delete(`woodware/prices/fixed_prices/${selectedPrice.id}/`).then(() => {
            this.$notify(setNotifyData(this.$i18n.t(`notifications.common.success`),
              this.$i18n.t(`notifications.prices.fixed_price_deleted`), 'success'));
            this.$store.dispatch('operation/loadFixedPrices', this.param);
            this.fetchPackagesData();
          });
        } else {
          this.$notify(setNotifyData(this.$i18n.t(`notifications.common.error`),
            this.$i18n.t(`notifications.prices.price_hasnt_been_deleted`), 'error'));
        }
      },
      deleteProcessPrice() {
        const selectedPrice = getFilteredSelectedData(this.operationCostGrid)[0];
        if (selectedPrice && selectedPrice.movement) {
          this.$http.delete(`woodware/prices/process_prices/${selectedPrice.id}/`).then(() => {
            this.$notify(setNotifyData(this.$i18n.t(`notifications.common.success`),
              this.$i18n.t(`notifications.prices.operation_cost_deleted`), 'success'));
            this.$store.dispatch('operation/loadOperationCosts', this.param);
            this.fetchPackagesData();
          }).catch(error => notificationsRenderer(error.data, 'error'));
        } else {
          this.$notify(setNotifyData(this.$i18n.t(`notifications.common.error`),
            this.$i18n.t(`notifications.prices.price_hasnt_been_deleted`), 'error'));
        }
      },
      async fetchAdditionalCostData(params) {
        const costId = params.data.id;
        try {
          const data = await this.$http.get(`woodware/acceptance_additional_costs/${costId}/`).then(handlerResponse);
          // TODO: here is overwriting of the translation name. perhaps, need fixes on back ends?
          this.openAdditionalCostModal({ ...data, type: { ...data.type, name: data.type.key } });
        } catch (e) {
          console.log(e);
        }
      },
      activeTab(grids, tabName) {
        this.resizeGridsColumns(grids);
        this.currentTabName = tabName;
      },
      resizeGridsColumns(grids) {
        for (let i = 0; i < grids.length; ++i) {
          setTimeout(() => {
            if (grids[i]) {
              autoResize(grids[i]);
            }
          }, 150);
        }
      },
      activeLabelsTab() {
        this.$nextTick(() => {
          this.resizeGridsColumns([this.labelsGrid]);
          this.labelsGrid.api.forEachNode((item) => {
            item.setSelected(true);
          });
        })
      },
      toStockSelectionChanged () {
        const loader = this.$loading.show();
        const allNodes = this.isPelletizing ? this.toStockGrid.api.getModel().nodeManager.allNodesMap : this.globalGridToStock.api.getModel().nodeManager.allNodesMap;
        const packets = Object.keys(allNodes).map((key) => allNodes[key])
          .filter(item => item.selected).map(item => item.data.id);
        const payload = {movement: this.param, packets};
        this.$http.post(`woodware/price_calculations/`, payload).then(handlerResponse).then(_ => {
          this.fetchPackagesData();
          loader.hide();
        }).catch(error => notificationsRenderer(error.data, 'error'));
      },
      toStockModify() {
        this.toStockGridSortedModel = this.toStockGrid.api.getSortModel();
        this.toStockGridFilteredModel = this.toStockGrid.api.getFilterModel();
        this.toStockPackagedModified = [];
        this.toStockGrid.api.forEachNodeAfterFilterAndSort(node => {
          !node.parent && this.toStockPackagedModified.push(node.data);
        });
      },
      deletePackages(grid) {
        const selectedRows = getFilteredSelectedNodes(grid);
        const packagesCount = selectedRows.filter(item => !item.parent).length;
        const selectedRowsData = selectedRows.map(item => item.data);
        if (selectedRowsData.length) {
          this.$swal.fire({
            title: this.$i18n.t(`alerts.warning`),
            text: this.$i18n.t(`alerts.delete_packages_confirmation`, {packagesCount}),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'OK'
          }).then(result => {
            if (result.value) {
              const body = {
                packages: selectedRowsData.map(item => item.id),
                current_movement_id: this.param
              };
              this.$http.delete(`woodware/movement_data/`, {body}).then(handlerResponse).then(response => {
                this.removingHandler(response);
                this.fetchPackagesData();
              }).catch(error => notificationsRenderer(error.data, 'error'));
            }
          });
        }
      },
      fetchGroupingData(params) {
        if (!this.isHistoryOperation) {
          if (!params.node.parent) {
            const gropingId = params.data.id;
            this.$http.get(`woodware/movement_groups/${gropingId}/`).then(handlerResponse).then(response => {
              this.openEditGroupingDialog(response);
            });
          }
        }
      },
      deleteGroupings() {
        const selectedGroupings = getFilteredSelectedData(this.groupingsGrid);
        if (selectedGroupings.length) {
          const body = {ids: selectedGroupings.map(item => item.id)};
          this.$http.delete('woodware/movement_groups/', {body}).then(handlerResponse).then(response => {
            this.removingHandler(response);
            this.fetchPackagesData();
            this.$store.dispatch('operation/loadOperationGroups', this.param);
          })
        } else {
          this.$notify(setNotifyData(this.$i18n.t(`notifications.operations.select_groupings_to_remove`), '', 'warn'));
        }
      },
      deleteAdditionalCost() {
        const selectedCosts = getFilteredSelectedData(this.additionalCostsGrid);
        if (selectedCosts.length) {
          const costId = selectedCosts[0].id;
          this.$http.delete(`woodware/acceptance_additional_costs/${costId}/`).then(handlerResponse).then(() => {
            this.$notify(setNotifyData(this.$i18n.t(`notifications.prices.additional_cost_deleted`), '', 'success'));
            this.fetchOperationData(false);
          })
        } else {
          this.$notify(setNotifyData(this.$i18n.t(`notifications.prices.additional_cost_to_delete`), '', 'warn'));
        }
      },
      removingHandler(response) {
        const type = response.fully_deleted ? 'success' : 'warn';
        this.$notify(setNotifyData(response.message, '', type));
      },
      selectLastPlacedPackets() {
        this.$http.get(`woodware/movements/${this.param}/movement_last_packets_moved_to_stock/`).then(handlerResponse)
          .then(response => {
            const lastOperations = response.packages_ids;
            this.labelsGrid.api.forEachNode((item) => {
              item.setSelected(false);
              lastOperations.forEach((elem) => {
                if (item.data.id === elem) {
                  item.setSelected(true);
                  this.labelsGrid.api.redrawRows([item]);
                }
              });
            });
          }).catch(error => notificationsRenderer(error.data, 'error'));
      },
      async getPdfLabels(payload) {
        try {
          const response = await this.$http.post(`woodware/packages_data_for_label/`, payload).then(handlerResponse);
          const win = window.open('', '_blank');
          const labelData = generateManyPagesForContent(response);
          pdfMake.createPdf(labelData).open({}, win);
        } catch (e) {
          notificationsRenderer(e.data, 'error');
        }
      },
      printLabels() {
        const selectedRows = getFilteredSelectedData(this.labelsGrid);
        if (selectedRows.length) {
          const payload = {
            columns: this.printLabelFields.map(item => item.field),
            packages: selectedRows.map(item => item.id),
          };
          this.$refs['labels-selected-columns-popover'].$emit('close');
          this.getPdfLabels(payload);
        } else {
          this.$notify(setNotifyData(this.$i18n.t(`notifications.packages.select_to_print`), '', 'warn'));
        }
      },
      fetchDocuments(cacheFlag) {
        if (cacheFlag) {
          this.$store.cache.dispatch('operation/loadDocumentsList', this.param);
        } else {
          this.$store.dispatch('operation/loadDocumentsList', this.param);
        }
      },
      openPartnerContactModal() {
        const contactData = {roles: [{key: 'delivery_contact', name: 'Delivery address'}]};
        this.$root.$emit('openPartnerContactModal', {...{isNew: true}, ...contactData});
      },
      openPriceEditModal(type, priceData) {
        const additionalPriceData = {};
        this.$root.$emit('openPriceEditingModal', {additionalPriceData, type, priceData});
      },
      openAddingPurchaseCostModal(data) {
        this.$root.$emit('openAddingPurchaseCostToPackageModal', data);
      },
      openAdditionalCostModal(costData) {
        this.$root.$emit('openAdditionalCostModal', {...costData});
      },
      openEditGroupingDialog(groupingData) {
        this.$root.$emit('openEditGroupingModal', groupingData);
      },
      openTemplatesModal() {
        this.$root.$emit('openDocumentsTemplatesModal', {movement_ids: this.param});
      },
      openHistoryModal() {
        this.$root.$emit('openHistoryModal', this.historySettings);
      },
      openHistoryPacketDialog(rowData) {
        if (!this.isActiveOperation) {
          if (rowData.data) {
            if (rowData.data.package_nr) {
              this.$root.$emit('openHistoryPacketModal', rowData);
            } else {
              this.$swal.fire('', this.$i18n.t(`alerts.not_generated_package_nr`), 'info');
            }
          } else {
            this.$swal.fire('', this.$i18n.t(`alerts.showing_package_history`), 'warning');
          }
        }
      },
      hideColumns(columns, hiddenColumns) {
        if (columns) {
          return columns.map(item => {
            if (hiddenColumns.includes(item.field)) {
              item.hide = true;
            }
            return item;
          });
        }
      },
      isNotEditableField(field) {
        if (this.operationData.non_editable_fields && this.operationData.non_editable_fields.length) {
          return this.operationData.non_editable_fields.includes(field);
        }
      },
      isHiddenFields(field) {
        if (this.operationData.hidden_fields && this.operationData.hidden_fields.length) {
          return this.operationData.hidden_fields.includes(field);
        }
      },
      adBlockCheck() {
        const testAd = document.createElement('div');
        testAd.innerHTML = '&nbsp;';
        testAd.className = 'adsbox';
        document.body.appendChild(testAd);
        setTimeout(() => {
          if (testAd.offsetHeight === 0) {
            this.isAdBlockOff = false;
          }
          testAd.remove();
        });
      },
      async fetchInitializeData() {
        this.operationDataInitialize();
        this.$store.cache.dispatch('woodware/getProcesses');
        this.$store.cache.dispatch('woodware/getProductionPlaces');
        this.$store.cache.dispatch('woodware/getUsers');
        this.$store.dispatch('woodware/getPartners', {is_active: true});
        this.$store.dispatch('woodware/loadShipmentDriverNames');
        this.$store.cache.dispatch('woodware/loadProductTemplates');
        this.fetchPurchaseContracts();
        const stock_addresses = await this.$store.cache.dispatch('woodware/getStockAddresses');
        if (stock_addresses.length) {
          this.operationData.stock_address = stock_addresses[0];
        }
        if (!this.isNewOperation) {
          await this.fetchOperationData();
        }
      },
      clearData() {
        const emptyData = {data: [], grid_name: ''};
        this.$store.commit('operation/setFromStockPackages', emptyData);
        this.$store.commit('operation/setToStockPackages', emptyData);
        if (this.documents.length) {
          this.$store.commit('operation/setDocumentsList', []);
        }
        this.$store.cache.delete('operation/loadDocumentsList', this.param);
      },
      removeSelectAllCheckbox(columnsList) {
        const columns = _.cloneDeep(columnsList);
        const checkboxColumn = columns.find(column => column.field === 'grouping');
        delete checkboxColumn.headerCheckboxSelection;
        return columns;
      },
      selectPacket(event) {
        this.$store.dispatch('operation/setIsSelectedPacket', event.target.checked);
      },
      changeFullWidth() {
        this.isGridsFullWidth = !this.isGridsFullWidth;
        localStorage.setItem('isGridFullWidth', this.isGridsFullWidth);
      }
    },
    computed: {
      ...mapState('woodware', ['processes', 'production_places', 'users', 'stock_addresses', 'shipment_driver_names',
        'product_templates']),
      ...mapState('operation', ['toStockPackages', 'fromStockPackages',
        'globalToStockTotalData', 'globalFromStockTotalData', 'fromStockTotalData', 'toStockTotalData',
        'operationGroupings', 'groupingsGridColumns', 'fixedPrices', 'scores',
        'operationCosts', 'additionalCostsGridColumns', 'labelsColumns', 'documents', 'toStockPinnedData',
        'fromStockPinnedData', 'fromStockGridName', 'toStockGridName', 'isSelectPacket']),
      ...mapState('pricePolicy', ['fixedPriceColumns', 'scoreColumns']),
      ...mapGetters('operation', ['fromStockColumns', 'toStockColumns', 'toStockGrid', 'fromStockGrid', 'globalGridFromStock', 'globalGridToStock',
        'groupingsGrid', 'fixedPriceGrid', 'scoresGrid', 'operationCostGrid', 'additionalCostsGrid', 'labelsGrid']),
      ...mapGetters('woodware', ['buyers', 'carriers', 'suppliers']),
      ...mapState('generic', ['permissionsList']),
      operationCostsColumns() {
        return this.fixedPriceColumns.concat({
          headerName: this.$i18n.t('tables.operation_cost'),
          field: 'whole_operation_price',
          width: 125
        })
      },
      optionsLabelColumns() {
        const labelColumns = [...this.labelsColumns];
        labelColumns.shift();
        const requiredToLabelColumns = ['package_nr'];
        return labelColumns.filter(item => !requiredToLabelColumns.find(elem => elem === item.field));
      },
      showingFromStockColumns() {
        let hiddenColumns = [];
        if (this.isProcessing && !this.isActiveOperation) {
          hiddenColumns = ['shipment_price'];
        } else if (this.isShipment && this.isActiveOperation) {
          hiddenColumns = ['per_tonne_price', 'process_price', 'process_price_per_tonne', 'per_pcs_price'];
        } else if (!this.isProcessing && this.isActiveOperation) {
          hiddenColumns = ['shipment_price', 'per_tonne_price', 'process_price', 'cost',
            'full_cost', 'process_price_per_tonne', 'per_pcs_price'];
        }
        return this.hideColumns(this.fromStockColumns, hiddenColumns);
      },
      globalShowingFromStockColumns() {
        if (this.showingFromStockColumns && this.showingFromStockColumns.length) {
          return this.removeSelectAllCheckbox(this.showingFromStockColumns);
        }
      },
      showingToStockColumns() {
        let hiddenColumns = [];
        if (this.isProcessing && !this.isActiveOperation) {
          hiddenColumns = ['purchase_price', 'penalty', 'penalty_perc'];
        } else if (!this.isShipment && this.isActiveOperation) {
          hiddenColumns = ['purchase_price', 'penalty', 'penalty_perc', 'per_tonne_price',
            'process_price', 'cost', 'full_cost', 'process_price_per_tonne', 'per_pcs_price'];
        }
        return this.hideColumns(this.toStockColumns, hiddenColumns);
      },
      globalShowingToStockColumns() {
        if (this.showingToStockColumns && this.showingToStockColumns.length) {
          return this.removeSelectAllCheckbox(this.showingToStockColumns);
        }
      },
      param() {
        return this.$route.params.id;
      },
      commonUrl() {
        return `woodware/movements/${this.param}/`;
      },
      isNewOperation() {
        return this.param === 'new';
      },
      operationStatus() {
        return this.operationData.status
      },
      operationStatusName() {
        return this.operationStatus.name || '';
      },
      isActiveOperation() {
        return this.operationData.status.key === 'A';
      },
      isApprovedOperation() {
        return this.operationData.status.key === 'P';
      },
      isHistoryOperation() {
        return this.operationData.status.key === 'C';
      },
      currentPackageIndex() {
        if (this.currentPackage) {
          if (this.toStockPackagedModified.length) {
            return this.toStockPackagedModified.findIndex(item => this.currentPackage.id === item.id);
          } else {
            return this.toStockPackages.findIndex(item => this.currentPackage.id === item.id);
          }
        }
      },
      isAccept() {
        return this.operationData.restrictions && this.operationData.restrictions.has_access_to_tabs === 'i';
      },
      isShipment() {
        return this.operationData.restrictions && this.operationData.restrictions.has_access_to_tabs === 'o';
      },
      isProcessing() {
        return this.operationData.restrictions && this.operationData.restrictions.has_access_to_tabs === 'io';
      },
      isPelletizing() {
        return this.operationData.restrictions && this.operationData.restrictions.has_access_to_tabs === 'i'
          && this.operationData.restrictions.type === 'pelletizing';
      },
      buyerId() {
        return this.operationData.distributor && this.operationData.distributor.id;
      },
      additionalCosts() {
        return this.operationData.additional_costs;
      },
      tabsRestrictions() {
        return {
          'global': this.isLoadedData && this.isProcessing,
          'from-stock': this.isLoadedData && (this.isActiveOperation && !this.isAccept
            || !this.isActiveOperation && this.isShipment),
          'to-stock': this.isLoadedData && (this.isActiveOperation && !this.isShipment
            || !this.isActiveOperation && this.isAccept),
          'grouping': this.isLoadedData && !this.isActiveOperation && this.isProcessing && !this.scores.length,
          'scores': this.isLoadedData && !this.isActiveOperation && this.isProcessing && this.scores.length,
          'fixed-price': this.isLoadedData && (!this.isActiveOperation && this.isProcessing
            || !this.isActiveOperation && this.isPelletizing),
          'operation-cost': this.isLoadedData && !this.isActiveOperation,
          'additional-cost': this.isLoadedData && (!this.isActiveOperation && this.isAccept && !this.isPelletizing),
          'labels': this.isLoadedData && !this.isShipment
        }
      },
      canBePartialCompleted() {
        return !this.isNewOperation && this.isActiveOperation
          && this.operationData.restrictions.can_be_partially_completed;
      },
      operationLabel() {
        return this.isNewOperation ? `${this.$i18n.t('labels.operations.new_operation')}`
          : `${this.$i18n.t('labels.common.operation')} / ${this.operationData.name} - ${this.operationStatusName}`;
      },
      operationInfo() {
        return this.isNewOperation ? '' : `${this.$i18n.t('common_content.creator')}: ${this.operationData.user}`;
      },
      historySettings() {
        return {url: `movement/?id=${this.param}`, title: this.operationLabel.split('/')[1]}
      },
      distributors() {
        const distributor = this.buyers.concat(this.suppliers);
        return _.uniqBy(distributor, 'name');
      },
      gridHeight() {
        if (window.innerHeight > 1024) {
          return this.showCollapse ? {height: 'calc(100vh - 575px)'} : {height: 'calc(100vh - 310px)'};
        } else {
          return this.showCollapse ? {height: '400px'} : {height: 'calc(100vh - 310px)'};
        }
      }
    },
    created() {
      this.isNewOperation && (this.showCollapse = true);
      const savedSelectPackage = localStorage.getItem('isSelectPacket');
      savedSelectPackage && (this.$store.dispatch('operation/setIsSelectedPacket', JSON.parse(savedSelectPackage)));
      const isGridFullWidthFromStorage = localStorage.getItem('isGridFullWidth');
      isGridFullWidthFromStorage && (this.isGridsFullWidth = JSON.parse(isGridFullWidthFromStorage));
      this.fetchInitializeData();
      this.adBlockCheck();
      const grids = [this.groupingsGridColumns, this.fixedPriceColumns, this.labelsColumns,
        this.additionalCostsGridColumns];
      grids.forEach(columnsList => {
        translateColumns.call(this, columnsList);
      });
    },
    beforeRouteUpdate(to, from, next) {
      if (to.params.id === 'new') {
        this.showCollapse = true;
        this.operationData.start_date = new Date();
      }
      next();
      this.fetchInitializeData();
    },
    beforeRouteLeave(to, from, next) {
      this.clearData();
      next();
    }
  }
</script>

<style scoped lang="scss">
  .grid {
    height: 450px;
  }

  .total-info__item {
    font-weight: bold;
    font-size: 13px;
  }

  .grid-label {
    min-height: 30px;
    margin-top: 7px;
    margin-bottom: 5px;
  }

  .off-auto-calc {
    border: 1px solid #f79494;
  }

  .card-body {
    padding-top: 10px;
  }

  .grid-size-toggle {
    margin-left: 10px;
    cursor: pointer;
  }

  input[type='file'] {
    display: none;
  }

  .ad-block-warning {
    font-size: 12px;
    margin-right: 5px;
    color: #fb8469;
  }

  .templates-list {
    max-width: 140px;
  }

  .package-settings-icon {
    color: #6c757d;
    transition: color .3s ease-in-out;

    &:hover {
      color: #868e96;
      cursor: pointer;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/modules/manufacture/operations/Operation.vue